import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { fetchAdminUserEmails } from "../../api/email";
import { Skel } from "../../components/Skeleton";
import { kindLabel, statusBadge, formatWhen } from "./AdminEmailsPage";

// Per-user drill-in for the Users drawer (spec 16): the last 20 emails and
// the account's opt-out state.

export function UserEmailsPanel({ userId }: { userId: string }) {
  const emailsQuery = useQuery({
    queryKey: ["admin/emails/users", userId],
    queryFn: () => fetchAdminUserEmails(userId),
  });
  const data = emailsQuery.data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>Emails</span>
        <Link to="/admin/emails" style={{ fontSize: 12, color: "var(--text-muted)", textDecoration: "none" }}>
          All emails
        </Link>
      </div>

      {emailsQuery.isLoading && <Skel height={60} />}

      {data && (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
            {(["activity", "digest", "product"] as const).map((category) => (
              <span
                key={category}
                style={{
                  padding: "2px 8px",
                  borderRadius: "var(--radius-pill)",
                  border: "1px solid var(--border-default)",
                  background: data.preferences[category] ? "var(--success-soft)" : "var(--surface-sunken)",
                  color: data.preferences[category] ? "var(--spruce-300)" : "var(--text-muted)",
                  textTransform: "capitalize",
                }}
              >
                {category} {data.preferences[category] ? "on" : "off"}
              </span>
            ))}
          </div>

          {data.entries.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No emails logged for this account.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.entries.map((e) => (
                <div
                  key={e.id}
                  title={e.error ?? e.subject}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    gap: 8,
                    alignItems: "center",
                    padding: "6px 0",
                    borderTop: "1px solid var(--border-subtle)",
                    fontSize: 12,
                  }}
                >
                  <span className="tnum" style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>{formatWhen(e.createdAt)}</span>
                  <span style={{ color: "var(--text-primary)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {kindLabel(e.kind)}
                    {e.subject && <span style={{ color: "var(--text-muted)" }}> · {e.subject}</span>}
                  </span>
                  {statusBadge(e.status)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
